/*
 * ServicePartnerList.tsx
 * This Service Partner list Component is used for representing all the saved partners in list Form.
 */
// Import necessary modules and components
import useFetch from "../../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../../components/page-header/page-header";
import GenericPopover from "../../../../components/pop-over";
import useToast from "../../../../hooks/useToast";
import Utils from "../../../../utils";
import { TResponseModel } from "../../../../types/config";
import { TFilterModel } from "../../../../types/config";
import { ePrivileges, eResultCode } from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { defaultFilterParams } from "../../../../utils/constants";
import {
  Button,
  Drawer,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
  Tooltip,
} from "antd";
import ServicePartnerForm from "./form";
import { TServicePartner } from "../../../../types";
import CustomDrawer from "../../../../components/drawer";
import {
  AddEditServicePartner,
  GetServicePartnerList,
} from "../../../../utils/api.constant";
import { useNavigate } from "react-router-dom";
import utils from "../../../../utils";
import ActionCard from "../../../../components/actionCard/actionCard";
import CustomModal from "../../../../components/modal";
import { UserContext } from "../../../../context";
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../../components/configurable-columns";
import ExportToCSV from "../../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../../utils/date";

/**
 * ServicePartnerList Component
 * This component renders the list of service partners, with
 * the ability to search, filter, and add new service partners.
 * It also includes a bulk upload feature for importing
 * service partners in bulk.
 *
 * @returns {JSX.Element} The JSX structure for the
 *         ServicePartnerList component.
 */
export default function ServicePartnerList() {
  // State and hooks initialization
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TServicePartner[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPartner, setSelectedPartner] =
    useState<TServicePartner | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "50%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "name"),
      sorter: (a: TServicePartner, b: TServicePartner) =>
        a.name.localeCompare(b.name),
      onFilter: (value: string | number | boolean, record: TServicePartner) =>
        record.name.includes(value as string),
    },
    {
      title: "Partner Code",
      dataIndex: "partnerCode",
      key: "partnerCode",
      width: "30%",
      filterSearch: true,
      sorter: (a: TServicePartner, b: TServicePartner) =>
        a.partnerCode.localeCompare(b.partnerCode),
      filters: utils.getUniqueFilters(data, "partnerCode"),
      onFilter: (value: string | number | boolean, record: TServicePartner) =>
        record.partnerCode.includes(value as string),
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    //   filters: [
    //     { text: "Active", value: "Active" },
    //     { text: "Inactive", value: "Inactive" },
    //   ],
    //   width: "10%",
    //   filterSearch: true,

    //   render: (isActive: number, record: TServicePartner) => (
    //     <Tag color={record.isActive ? "var(--bs-primary)" : "#f50"}>
    //       {record.isActive ? "Active" : "Inactive"}
    //     </Tag>
    //   ),

    //   sorter: (a: TServicePartner, b: TServicePartner) =>
    //     a?.status.localeCompare(b?.status),
    //   onFilter: (value: string | number | boolean, record: TServicePartner) =>
    //     record.status.includes(value as string),
    // },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "5%",
      render: (partner: TServicePartner) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}
                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.EDITPARTNERS
                ) ? (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={ServicePartnerForm} // Passing ServicePartner component
                    componentProps={{
                      id: partner.id, // Pass necessary props for ServicePartner
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Service Partner Form"
                  />
                ) : null}

                {/* {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.DELETEPARTNERS
                ) ? (
                  <ActionCard
                    // icon={partner.isActive ? faUserTimes : faUser}
                    text={partner.isActive ? "Inactive" : "Active"}
                    onClick={() => showModal(partner)}
                  >
                    {partner.isActive ? (
                      <UserDeleteOutlined />
                    ) : (
                      <UserAddOutlined />
                    )}
                  </ActionCard>
                ) : null} */}
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState(columns);

  const context: any = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Fetches the list of service partners from the API.
   * Sets the isLoading state to true, sends a request with the current filterParams, and sets the data and filterParams state with the response.
   * If the response is not successful, shows an error toast.
   * Finally, sets isLoading to false.
   */
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetServicePartnerList,
        requestPayload
      );
      const { dataResponse, data, filterModel } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map(
          (item: TServicePartner, index: number) => ({
            key: (item.id ?? "").toString(),
            srNo: index + 1,
            name: item.name,
            partnerCode: item.partnerCode,
            status: item?.isActive ? "Active" : "Inactive",
            id: item.id,
            isActive: item.isActive,
          })
        );
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (dataset: TServicePartner[]) => {
    const nameFilters = utils.getUniqueFilters(dataset, "name");
    const partnerCodeFilters = utils.getUniqueFilters(dataset, "partnerCode");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "name") return { ...col, filters: nameFilters };
        if (col.key === "partnerCode")
          return { ...col, filters: partnerCodeFilters };
        return col;
      })
    );
  };

  const filteredData = data.filter((item) => {
    const nameMatch =
      !activeFilters.name ||
      activeFilters.name.includes(item.name);
    const partnerCodeMatch =
      !activeFilters.partnerCode ||
      activeFilters.partnerCode.includes(item.partnerCode);

    const searchMatch = Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase())
    );
    return nameMatch && partnerCodeMatch && searchMatch;
  });

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleOk = (partnerDetails: TServicePartner) => {
    updatePartnerDetails(partnerDetails);
    setSelectedPartner(null);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setSelectedPartner(null);
    setIsModalVisible(false);
  };

  const showModal = (partner: TServicePartner) => {
    setSelectedPartner(partner);
    setIsModalVisible(true);
  };

  /**
   * Updates the dealer with the given values.
   * Sends a request to the API with the given values, and if the response is successful, shows a success toast and fetches the data again.
   * If the response is not successful, shows an error toast with the description from the response.
   * @param {TDealer} values - the dealer values to update.
   */
  const updatePartnerDetails = async (values: TServicePartner) => {
    try {
      const { isActive, ...restValues } = values;
      const payload = {
        data: {
          ...restValues,
          isActive: isActive ? 1 : 0,
        },
      };
      const response = await post(AddEditServicePartner, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      console.log("Finally");
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
    isActive: user.isActive ? "Active" : "Inactive",
  }));

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };
  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Service Partners`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDPARTNERS
          ) ? (
            <CustomDrawer
              size="default"
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={ServicePartnerForm} // Passing Service Partner Form component
              componentProps={{
                id: 0, // Pass necessary props for ServicePartnerForm
                onRefreshList: () => {
                  fetchData();
                },
              }}
              title="Service Partner Form"
            />
          ) : null}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`service_partner_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>

      <CustomModal
        title="Confirm"
        isOpen={isModalVisible} // Ensure modal is open only when selectedUserId matches
        onOk={() =>
          handleOk({
            ...selectedPartner,
            isActive: !selectedPartner?.isActive,
          } as any)
        } // Passing the custom function
        onCancel={() => handleCancel()}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          You are about to{" "}
          {selectedPartner?.isActive ? "Deactivate" : "Activate"}{" "}
          <b>{selectedPartner?.name}</b>
        </p>
      </CustomModal>
    </div>
  );
}
